import React, {
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react'
import {
  Button,
  DateRangePicker,
  PageHeader,
  Tooltip,
  Table,
} from '@demandlocal/components'
import LineChart from '../../../../../components/Charts/LineChart'
import { ReactComponent as DownloadIcon } from '../../../../../images/icons/action_download.svg'
import { ReactComponent as VehicleIcon } from '../../../../../images/icons/vehicle_single.svg'
import { useParams } from 'react-router'
import { formatCurrency, formatNumber } from '../../../../../lib/utils/formatNumber'
import { createColumnHelper } from '@tanstack/react-table'
import PolarAreaChart from '../../../../../components/Charts/PolarAreaChart'
import { useSearchParams } from 'react-router-dom'
import { useDownloadPDFReport } from '../../../../../api/dealership/reports/useDownloadPDFReport'
import toast from 'react-hot-toast'
import { useGetDisplayCampaignById } from '../../../../../api/dealership/campaigns/useGetDisplayCampaignById'
import { useGetVLACampaignReports } from '../../../../../api/dealership/campaigns/reports/useGetVLACampaignReports'
import { useGetVLACampaignVehicleMetrics } from '../../../../../api/dealership/campaigns/reports/useGetVLACampaignVehicleMetrics'
import { Vehicle } from '../../../../../lib/types/Vehicle'
import { DateRange } from '../../../../../lib/types/DateRange'
import { GA4 } from '../../../../../lib/utils/ga4'
import { useDealership } from '../../../../../store/zustand/dealershipStore'
import { useExportToXLS } from '../../../../../lib/hooks/useExportToXLS'
import GhostButton from '../../../../../components/Buttons/GhostButton'
import { ImpressionLineChart } from '../../../../../components/Charts/Reports/ImpressionLineChart'
import { ClickLineChart } from '../../../../../components/Charts/Reports/ClickLineChart'
import mtm from '../../../../../lib/utils/mtm'
import { useDateRange } from '../../../../../store/zustand/dateRangeStore'
import { useGetSearchCampaignReports } from '../../../../../api/dealership/campaigns/reports/useGetSearchCampaignReports'
import { useGetCampaignCallRecordings } from '../../../../../api/dealership/campaigns/reports/useGetCampaignCallRecordings'
import dayjs from 'dayjs'
import downloadCSV from '../../../../../api/downloadCSV'
import { useGetCampaignBudget } from '../../../../../api/dealership/campaigns/useGetCampaignBudget'
import { useGetAgencyByAgencyId } from '../../../../../api/agencies/useGetAgencyByAgencyId'
import SearchConversionMetrics from '../../../../../components/Campaigns/Reports/SearchConversionMetrics'

const columnHelper = createColumnHelper<Vehicle>()
const callsColumnHelper = createColumnHelper<{
  phone_number: string
  recording: string
  datetime: string
  duration: string
  phone_call_vendor_id: string
}>()

const AudienceCampaignTemplate = ({
  children,
  filters,
  setFilters,
}: PropsWithChildren<{
  filters: DateRange
  setFilters: Dispatch<SetStateAction<DateRange>>
}>): JSX.Element => {
  const { campaign_id } = useParams()
  const { setDateRange } = useDateRange()
  const dealership = useDealership()
  const { data: campaign } = useGetDisplayCampaignById({ campaign_id: campaign_id })
  const { data: agency } = useGetAgencyByAgencyId(dealership?.organization_id)
  const { data: campaignBudget } = useGetCampaignBudget({
    campaign_id,
    filters: {
      start_date: filters?.dateRange.startDate,
      end_date: filters?.dateRange.endDate,
    },
  })
  const {
    data: {
      overallMetrics: sales = {
        sales: 0,
      },
    } = {},
    isLoading: isLoadingReport,
  } = useGetVLACampaignReports({
    campaign_id: campaign_id,
    filters: {
      start_date: filters?.dateRange?.startDate,
      end_date: filters?.dateRange?.endDate,
    },
  })

  const {
    data: {
      overallMetrics = {
        impressions: 0,
        clicks: 0,
        conversions: 0,
        phone_calls: 0,
        sales: 0,
      },
    } = {},
  } = useGetSearchCampaignReports({
    campaign_id: campaign_id,
    filters: {
      start_date: filters?.dateRange?.startDate,
      end_date: filters?.dateRange?.endDate,
    },
  })

  const { data: vehicles, isLoading: isLoadingVehicles } =
    useGetVLACampaignVehicleMetrics({
      campaign_id: campaign_id,
      filters: {
        start_date: filters?.dateRange?.startDate,
        end_date: filters?.dateRange?.endDate,
      },
    })

  const { mutateAsync: downloadReport, isLoading: isDownloadingReport } =
    useDownloadPDFReport()

  const onDateRangeChange = (dateRange: DateRange): void => {
    setFilters((prev: DateRange) => ({
      ...prev,
      ...dateRange,
    }))
    setDateRange(dateRange)
  }

  useEffect(() => {
    if (!isLoadingReport && !isLoadingVehicles) {
      const content = document.getElementById('content')
      if (content) content.className = `${content.className} loaded`
    }
  }, [isLoadingReport, isLoadingVehicles])

  return (
    <div className={'flex flex-1 flex-col'} id={'content'}>
      <PageHeader
        backRoute={'../'}
        title={`${dealership?.dealership} - ${
          campaign?.client_campaign_display_name ?? ''
        }`}
        {...(Boolean(agency?.show_budget_type) && {
          infoPill: {
            type: 'warning',
            content: `Budget: ${
              agency?.show_budget_type === 1
                ? formatCurrency(campaignBudget?.demand_local_net)
                : formatCurrency(campaignBudget?.gross_revenue)
            }`,
          },
        })}
        cardsOptions={[
          {
            title: 'Total Impressions',
            content: formatNumber(overallMetrics?.impressions || 0),
            color: 'bg-palatinate-blue/30',
          },
          {
            title: 'Total Clicks',
            content: formatNumber(overallMetrics?.clicks || 0),
            color: 'bg-dodger-blue/30',
          },
          {
            title: 'Phone calls',
            content: formatNumber(overallMetrics?.phone_calls || 0),
            color: 'bg-kelly-green/30',
          },
          {
            title: ({ collapsed }) => {
              return collapsed ? (
                <div>Attributed sales</div>
              ) : (
                <div className={'flex flex-row'}>
                  <span>Attributed sales</span>
                  <span className={'absolute right-0'}>
                    <VehicleIcon />
                  </span>
                </div>
              )
            },
            content: formatNumber(sales?.sales || 0),
            color: '#FFFFFF',
            border: true,
          },
        ]}
        rightSlot={({ hidden }) => (
          <div className={'print:hidden flex flex-row gap-2'}>
            <DateRangePicker
              collapsed={hidden}
              filters={filters}
              onChange={onDateRangeChange}
            />
            <Button
              disabled={isDownloadingReport}
              collapsed={hidden}
              leftSlot={<DownloadIcon className={'fill-white'} />}
              onClick={async (e) => {
                e.preventDefault()
                const toastId = toast.loading('Generating report...')
                GA4.sendEvent({
                  event_name: 'export_pdf',
                  event_params: {
                    campaign_type: 'VLA',
                    campaign_id: campaign?.client_campaign_id,
                  },
                })
                mtm.sendEvent('Export', 'PDF', 'VLA', campaign?.client_campaign_id)
                await downloadReport(undefined, {
                  async onSuccess(data) {
                    toast.success('Report generated successfully', { id: toastId })
                    const blob = await data.blob()
                    const url = window.URL.createObjectURL(blob)
                    const a = document.createElement('a')
                    a.href = url
                    a.download = `${campaign?.client_campaign_display_name}_${filters?.dateRange?.startDate}_-_${filters?.dateRange?.endDate}.pdf`
                    document.body.appendChild(a) // we need to append the element to the dom -> otherwise it will not work in firefox
                    a.click()
                    a.remove()
                  },
                })
              }}>
              Export as PDF
            </Button>
          </div>
        )}
      />
      <div className={'flex flex-1 flex-col px-10 max-w-[1920px] mx-auto w-full'}>
        <div className={'mt-12 mb-10'} />
        {children}
      </div>
    </div>
  )
}

const VLACampaign = (): JSX.Element => {
  const { campaign_id } = useParams()
  const { dateRange } = useDateRange()
  const [queryParams, setQueryParams] = useSearchParams()
  const { exportToCSV } = useExportToXLS()
  const { data: campaign } = useGetDisplayCampaignById({ campaign_id: campaign_id })
  const dealership = useDealership()
  const [downloadingTable, setDownloadingTable] = useState<boolean>(false)
  const [downloadingCalls, setDownloadingCalls] = useState<boolean>(false)

  const [filters, setFilters] = useState<DateRange>({
    dateRange: {
      startDate: queryParams.get('startDate') || dateRange.dateRange.startDate,
      endDate: queryParams.get('endDate') || dateRange.dateRange.endDate,
    },
  })

  const {
    data: { vehiclePerformance } = { vehiclePerformance: { year: [], make: [] } },
    isLoading: isLoadingReport,
    isPreviousData: isPreviousReportsData,
  } = useGetVLACampaignReports({
    campaign_id: campaign_id,
    filters: {
      start_date: filters.dateRange.startDate,
      end_date: filters.dateRange.endDate,
    },
  })

  const {
    data: { metrics } = { metrics: [] },
    data: { conversions } = { conversions: [] },
    isPreviousData: isPreviousSearchReport,
    isLoading: isLoadingSearchReport,
  } = useGetSearchCampaignReports({
    campaign_id: campaign_id,
    filters: {
      start_date: filters.dateRange.startDate,
      end_date: filters.dateRange.endDate,
    },
  })

  useEffect(() => {
    setQueryParams(filters.dateRange)
  }, [filters, setQueryParams])

  const vehicleMetricsColumns = useMemo(
    () => [
      columnHelper.accessor('vin_number', {
        header: 'VIN',
        cell: ({ getValue }) => getValue(),
      }),
      columnHelper.accessor('description', {
        header: 'Vehicle',
        cell: ({ getValue }) => getValue(),
      }),
      columnHelper.accessor('impressions', {
        header: 'Impressions',
        cell: ({ getValue }) => formatNumber(getValue() ?? 0),
      }),
      columnHelper.accessor('clicks', {
        header: 'Clicks',
        cell: ({ getValue }) => formatNumber(getValue() ?? 0),
      }),
      columnHelper.accessor('ctr', {
        header: 'CTR',
        cell: ({ getValue }) => `${getValue()}%`,
      }),
    ],
    [],
  )

  const phoneCallsColumns = useMemo(
    () => [
      callsColumnHelper.accessor('phone_number', {
        header: 'Caller',
        cell: (info) => info.getValue(),
      }),
      callsColumnHelper.accessor('phone_call_vendor_id', {
        header: 'Recording',
        cell: (info) => (
          <a
            rel={'noreferrer'}
            target={'_blank'}
            className={'font-bold text-primary-persian-blue'}
            href={`https://dealers.digitalads.report/campaign/recording/${info.getValue()}`}>
            Listen to recording
          </a>
        ),
      }),
      callsColumnHelper.accessor('datetime', {
        header: 'Date',
        cell: (info) => dayjs(info.getValue()).format(`MMMM D, YYYY - h:mm a`),
      }),
      callsColumnHelper.accessor('duration', {
        header: 'Duration',
        cell: (info) => info.getValue(),
      }),
    ],
    [],
  )

  return (
    <AudienceCampaignTemplate filters={filters} setFilters={setFilters}>
      <div className={'flex flex-col gap-10 pb-8'}>
        <div className={'flex flex-col gap-8'}>
          <div className={'flex grid grid-cols-2 gap-5'}>
            <ImpressionLineChart
              data={metrics}
              isLoading={isLoadingReport}
              isPrevious={isPreviousReportsData}
              sheetTitle={campaign?.client_campaign_display_name}
            />
            <ClickLineChart
              data={metrics}
              isLoading={isLoadingReport}
              isPrevious={isPreviousReportsData}
              sheetTitle={campaign?.client_campaign_display_name}
            />
          </div>
          <div className={'flex grid grid-cols-2 gap-5'}>
            <div className={'min-h-[270px] border-2 rounded-xl'}>
              <div
                className={
                  'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
                }>
                <span>Clicks by vehicle year</span>
                <div>
                  <GhostButton
                    transparent={true}
                    onClick={(e) => {
                      e.preventDefault()
                      exportToCSV(
                        vehiclePerformance?.year?.map(
                          (data: {
                            year: string
                            impressions: number
                            clicks: number
                          }) => {
                            return {
                              'Vehicle year': data.year,
                              Impressions: data.impressions,
                              Clicks: data.clicks,
                            }
                          },
                        ),
                        `${campaign?.client_campaign_display_name.replaceAll(
                          ' ',
                          '',
                        )}_stats-by-year_${filters.dateRange.startDate}-${
                          filters.dateRange.endDate
                        }.csv`,
                        false,
                        'csv',
                      )
                    }}>
                    <Tooltip label={'Download'}>
                      <div>
                        <DownloadIcon fill="stroke-black" />
                      </div>
                    </Tooltip>
                  </GhostButton>
                </div>
              </div>
              <div className={'h-[270px] p-2'}>
                <PolarAreaChart
                  isPreviousData={isPreviousReportsData}
                  isLoading={isLoadingReport}
                  data={
                    vehiclePerformance?.year?.map(
                      (data: { year: string; clicks: number }) => {
                        return {
                          label: data.year,
                          value: data.clicks > 0 ? data.clicks : 0, //chart.js gives error if value is 0
                        }
                      },
                    ) ?? []
                  }
                />
              </div>
            </div>
            <div className={'min-h-[270px] border-2 rounded-xl'}>
              <div
                className={
                  'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
                }>
                <span>Clicks by vehicle make</span>
                <div>
                  <GhostButton
                    transparent={true}
                    onClick={(e) => {
                      e.preventDefault()
                      exportToCSV(
                        vehiclePerformance?.make?.map(
                          (data: {
                            make: string
                            impressions: number
                            clicks: number
                          }) => {
                            return {
                              'Vehicle make': data.make,
                              Impressions: data.impressions,
                              Clicks: data.clicks,
                            }
                          },
                        ),
                        `${campaign?.client_campaign_display_name.replaceAll(
                          ' ',
                          '',
                        )}_stats-by-make_${filters.dateRange.startDate}-${
                          filters.dateRange.endDate
                        }.csv`,
                        false,
                        'csv',
                      )
                    }}>
                    <Tooltip label={'Download'}>
                      <div>
                        <DownloadIcon fill="stroke-black" />
                      </div>
                    </Tooltip>
                  </GhostButton>
                </div>
              </div>
              <div className={'h-[270px] p-2'}>
                <PolarAreaChart
                  isPreviousData={isPreviousReportsData}
                  isLoading={isLoadingReport}
                  data={
                    vehiclePerformance?.make?.map(
                      (data: { make: string; clicks: number }) => {
                        return {
                          label: data.make,
                          value: data.clicks > 0 ? data.clicks : 1,
                        }
                      },
                    ) ?? []
                  }
                />
              </div>
            </div>
          </div>
          <SearchConversionMetrics
            conversions={conversions}
            isLoading={isLoadingSearchReport}
            isPreviousData={isPreviousSearchReport}
          />
          <div className={'pb-5 border-2 rounded-lg flex flex-col gap-3'}>
            <div
              className={
                'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
              }>
              <span>CTR</span>
              <div className={'flex items-center gap-2'}>
                {metrics.length > 0 &&
                  `${
                    Math.round(
                      (metrics.map((item) => item.clicks).reduce((a, b) => a + b) *
                        10000) /
                        metrics.map((item) => item.impressions).reduce((a, b) => a + b),
                    ) / 100
                  }%`}
              </div>
            </div>
            <div className={'w-full h-[320px] p-2'}>
              <LineChart
                data={metrics ?? []}
                gradient={'blue-to-cyan'}
                yKey={'ctr'}
                isLoading={isLoadingReport}
                isPreviousData={isPreviousReportsData}
              />
            </div>
          </div>
          <div className={'flex flex-col gap-4'}>
            <div className="flex flex-row justify-between items-center">
              <span className={'font-bold text-primary-xiketic text-sm'}>
                Top Performing Vehicles
              </span>
              <Button
                disabled={downloadingTable}
                variant="primary"
                onClick={async (e) => {
                  e.preventDefault()
                  const toastId = toast.loading('Downloading...')
                  setDownloadingTable(true)
                  try {
                    const headers = vehicleMetricsColumns.map((column) => ({
                      // @ts-ignore
                      accessorKey: column.accessorKey,
                      header: column.header,
                    }))
                    await downloadCSV({
                      url: `/agencies/${dealership?.organization_id}/dealership/${dealership?.id}/campaigns/search/vla/${campaign_id}/vehicles`,
                      fetchOptions: {
                        campaign_id: campaign_id,
                        start_date: filters?.dateRange?.startDate,
                        end_date: filters?.dateRange?.endDate,
                      },
                      headers: headers,
                      fileName: `${campaign.client_campaign_display_name}_top_performing_vehicles_${filters.dateRange.startDate}-${filters.dateRange.endDate}.csv`,
                    })
                    toast.success('Downloaded table!', { id: toastId, duration: 5000 })
                    setDownloadingTable(false)
                  } catch (err) {
                    toast.error('Failed to download table at this time.', {
                      id: toastId,
                      duration: 5000,
                    })
                    setDownloadingTable(false)
                  }
                }}>
                Export CSV
              </Button>
            </div>
            <Table
              emptyLabel={'No vehicles found'}
              columns={vehicleMetricsColumns}
              filters={{
                campaign_id: campaign_id,
                start_date: filters?.dateRange?.startDate,
                end_date: filters?.dateRange?.endDate,
              }}
              fetchFn={useGetVLACampaignVehicleMetrics}
            />
          </div>
          <div className={'flex flex-col gap-4'}>
            <div className="flex flex-row justify-between items-center">
              <span className={'font-bold text-primary-xiketic text-sm'}>
                Phone calls
              </span>
              <Button
                disabled={downloadingCalls}
                variant="primary"
                onClick={async (e) => {
                  e.preventDefault()
                  const toastId = toast.loading('Downloading...')
                  setDownloadingCalls(true)
                  try {
                    const headers = phoneCallsColumns.map((column) => ({
                      accessorKey:
                        // @ts-ignore
                        column.accessorKey === 'phone_call_vendor_id'
                          ? 'recording'
                          : // @ts-ignore
                            column.accessorKey,
                      header: column.header,
                    }))
                    await downloadCSV({
                      url: `/agencies/${dealership?.organization_id}/dealership/${dealership?.id}/campaigns/search/${campaign_id}/calls`,
                      fetchOptions: {
                        campaign_id: campaign_id,
                        start_date: filters?.dateRange?.startDate,
                        end_date: filters?.dateRange?.endDate,
                      },
                      headers: headers,
                      fileName: `${campaign.client_campaign_display_name}_phone_calls_${filters.dateRange.startDate}-${filters.dateRange.endDate}.csv`,
                    })
                    toast.success('Downloaded table!', { id: toastId, duration: 5000 })
                    setDownloadingCalls(false)
                  } catch (err) {
                    toast.error('Failed to download table at this time.', {
                      id: toastId,
                      duration: 5000,
                    })
                    setDownloadingCalls(false)
                  }
                }}>
                Export CSV
              </Button>
            </div>{' '}
            <Table
              emptyLabel={'No phone calls found'}
              columns={phoneCallsColumns}
              filters={{
                campaign_id: campaign_id,
                start_date: filters?.dateRange?.startDate,
                end_date: filters?.dateRange?.endDate,
              }}
              customPageSize={5}
              defaultSorting={[{ id: 'datetime', desc: true }]}
              fetchFn={useGetCampaignCallRecordings}
            />
          </div>
        </div>
      </div>
    </AudienceCampaignTemplate>
  )
}

export default VLACampaign
